// Export isn't offered - the icon goes with its button below.
import { ChartLine, /* Export, */ SidebarSimple } from '@phosphor-icons/react';
import { useGetAnalytics } from '@reelplicate/api-client/analytics';
import type { AnalyticsRange } from '@reelplicate/api-client/model';
import { useListPersonas } from '@reelplicate/api-client/personas';
import { useListSocialAccounts } from '@reelplicate/api-client/social-accounts';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Button,
  DatePickerContent,
  type DateRange,
  EmptyState,
  IconButton,
  Inline,
  Popover,
  PopoverAnchor,
  PopoverContent,
  SegmentedControl,
  SegmentedControlItem,
  Skeleton,
  Stack,
} from '@reelplicate/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { groupAccounts } from '@/features/calendar/calendar-model';
import { PLATFORM_NAMES } from '@/features/calendar/platforms';
import { useCheckAccount } from '@/features/settings/check-account';

import { AccountsTable } from './accounts-table';
import { ALL_CHANNELS, type Channel, ChannelsPanel } from './channels-panel';
import { formatDay } from './format';
import { MetricCard } from './metric-card';
import { METRIC_META, parseDay, toIsoDay } from './metrics';

const RANGES = [
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: 'month', label: 'This month' },
  { value: 'custom', label: 'Custom' },
];

/** A preset window ending today, the month so far, or days picked in the calendar (both included). */
type Period =
  | { kind: 'preset'; range: AnalyticsRange }
  | { kind: 'month' }
  | { kind: 'custom'; from: Date; to: Date };

/** Lists load for the panel and the table; one failing shouldn't take the page down. */
const QUIET = (workspaceId: string) => ({
  query: { enabled: Boolean(workspaceId), throwOnError: false },
});

/**
 * Figma "Screen — Analytics" (166:3): growth over the last 7, 30 or 90 days -
 * six metric cards and a table by account - for every account or the one
 * picked in Channels, this month so far, or over a custom range picked in a calendar. Numbers
 * come from the account stats sync. There's no Export.
 */
export function AnalyticsPage({ now = new Date() }: { now?: Date } = {}) {
  const [channel, setChannel] = useState<Channel>(ALL_CHANNELS);
  const [period, setPeriod] = useState<Period>({ kind: 'preset', range: '30d' });
  const [pickerOpen, setPickerOpen] = useState(false);
  const rangeParams =
    period.kind === 'preset'
      ? { range: period.range }
      : period.kind === 'month'
        ? {
            from: toIsoDay(new Date(now.getFullYear(), now.getMonth(), 1)),
            to: toIsoDay(now),
          }
        : { from: toIsoDay(period.from), to: toIsoDay(period.to) };
  const [panelOpen, setPanelOpen] = useState(true);
  const navigate = useNavigate();

  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const workspaceId = workspace?.id ?? '';
  const { data: socialAccounts } = useListSocialAccounts(workspaceId, QUIET(workspaceId));
  const { data: personas } = useListPersonas(workspaceId, QUIET(workspaceId));
  // Every account's report tells Channels which are tracked; a picked
  // account gets its own.
  const { data: everyone } = useGetAnalytics(
    workspaceId,
    rangeParams,
    QUIET(workspaceId),
  );
  const { data: picked } = useGetAnalytics(
    workspaceId,
    { ...rangeParams, accountId: channel },
    { query: { enabled: Boolean(workspaceId) && channel !== ALL_CHANNELS } },
  );
  const report = channel === ALL_CHANNELS ? everyone : picked;

  const { groups, accounts: allAccounts } = useMemo(() => {
    const grouped = groupAccounts(
      { name: workspace?.name ?? 'Brand', logoUrl: workspace?.logoUrl },
      personas?.items ?? [],
      socialAccounts ?? [],
    );
    const used = new Set(grouped.accounts.map((a) => a.groupId));
    return {
      groups: grouped.groups.filter((g) => used.has(g.id)),
      accounts: grouped.accounts,
    };
  }, [workspace?.name, workspace?.logoUrl, personas, socialAccounts]);
  const tracked = useMemo(
    () => new Set(everyone?.accounts.filter((a) => a.tracked).map((a) => a.accountId)),
    [everyone],
  );
  const stats = useMemo(
    () => new Map(report?.accounts.map((a) => [a.accountId, a])),
    [report],
  );

  const account = allAccounts.find((a) => a.id === channel);
  const accounts = account ? [account] : allAccounts;
  const connect = () => navigate('/settings/connected-accounts');
  const check = useCheckAccount(workspaceId);
  const readStates = useMemo(
    () => new Map((socialAccounts ?? []).map((a) => [a.id, a])),
    [socialAccounts],
  );
  const start = report ? parseDay(report.range.start) : null;
  const end = report ? parseDay(report.range.end) : null;
  const days =
    report?.range.days ??
    (period.kind === 'preset' ? Number.parseInt(period.range) : null);
  const dates = start && end ? `${formatDay(start)} – ${formatDay(end)}` : null;

  return (
    <div className="flex h-full">
      {panelOpen && (
        <ChannelsPanel
          groups={groups}
          accounts={allAccounts}
          tracked={tracked}
          selected={channel}
          onSelect={setChannel}
          onCollapse={() => setPanelOpen(false)}
          onConnectAccount={connect}
        />
      )}

      {/* Children keep their height and the column scrolls: cards with
          overflow-hidden would otherwise shrink to fit and clip. */}
      <Stack gap={6} className="min-w-0 flex-1 overflow-y-auto *:shrink-0 p-8">
        <Inline as="header" gap={3} wrap>
          {!panelOpen && (
            <IconButton
              icon={SidebarSimple}
              variant="outline"
              aria-label="Show channels"
              onClick={() => setPanelOpen(true)}
            />
          )}
          <Stack gap={1} className="min-w-0 flex-1">
            <h1 className="text-heading-h1 text-text-primary truncate">
              {account
                ? `${account.handle} · ${PLATFORM_NAMES[account.platform]}`
                : 'All accounts'}
            </h1>
            <p className="text-body-md text-text-secondary">
              {period.kind === 'preset'
                ? `Gained in the last ${days} days${dates ? ` · ${dates}` : ''}`
                : period.kind === 'month'
                  ? `Gained this month${dates ? ` · ${dates}` : ''}`
                  : `Gained ${dates ?? `${formatDay(period.from)} – ${formatDay(period.to)}`}${days ? ` (${days} days)` : ''}`}
              {days && `, vs the previous ${days} days`}
            </p>
          </Stack>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverAnchor asChild>
              <SegmentedControl
                aria-label="Date range"
                value={period.kind === 'preset' ? period.range : period.kind}
                onValueChange={(value) => {
                  if (value === 'month') {
                    setPeriod({ kind: 'month' });
                  } else if (value !== 'custom') {
                    setPeriod({ kind: 'preset', range: value as AnalyticsRange });
                  }
                }}
                className="gap-0.5 rounded-[10px] p-[3px]"
              >
                {RANGES.map(({ value, label }) => (
                  <SegmentedControlItem
                    key={value}
                    value={value}
                    // Custom opens the calendar, and reopens it to change the days.
                    onClick={value === 'custom' ? () => setPickerOpen(true) : undefined}
                    className="rounded-lg px-3 py-1.5"
                  >
                    {label}
                  </SegmentedControlItem>
                ))}
              </SegmentedControl>
            </PopoverAnchor>
            <PopoverContent
              align="end"
              aria-label="Custom range"
              // The calendar focuses its day itself.
              onOpenAutoFocus={(e) => e.preventDefault()}
              className="min-w-0 p-3"
            >
              <DatePickerContent
                type="range"
                autoFocus
                today={now}
                disabled={{ after: now }}
                value={
                  period.kind === 'custom'
                    ? { from: period.from, to: period.to }
                    : undefined
                }
                onValueChange={(picked?: DateRange) => {
                  if (picked?.from && picked.to) {
                    setPeriod({ kind: 'custom', from: picked.from, to: picked.to });
                  }
                  setPickerOpen(false);
                }}
                onCancel={() => setPickerOpen(false)}
              />
            </PopoverContent>
          </Popover>
          {/* Export isn't offered.
          <Button variant="outline" leadingIcon={Export} onClick={...}>
            Export
          </Button> */}
        </Inline>

        {!report || !start ? (
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Object.keys(METRIC_META).map((id) => (
              <Skeleton
                key={id}
                shape="rectangle"
                shimmer
                className="h-[218px] rounded-2xl"
              />
            ))}
          </div>
        ) : tracked.size === 0 ? (
          <EmptyState
            icon={ChartLine}
            titleAs="h2"
            title="No numbers yet"
            description={
              allAccounts.length === 0
                ? 'Add the accounts you post to, and their growth shows up here.'
                : 'Growth shows up here once your accounts have synced.'
            }
            actions={<Button onClick={connect}>Connected accounts</Button>}
          />
        ) : (
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {report.metrics.map((metric) => (
              <MetricCard
                key={metric.id}
                label={METRIC_META[metric.id].label}
                color={METRIC_META[metric.id].color}
                total={metric.total}
                change={metric.change}
                daily={metric.daily}
                start={start}
              />
            ))}
          </div>
        )}

        {report && accounts.length > 0 && (
          <AccountsTable
            groups={groups}
            accounts={accounts}
            stats={stats}
            todayIndex={report.todayIndex}
            now={now}
            onConnect={connect}
            readStates={readStates}
            onCheck={(a) => void check.run(a)}
            checking={check.pending ? check.checking : undefined}
          />
        )}
      </Stack>
    </div>
  );
}
